import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { requestStaffPasswordReset, requestStaffSalonIds } from "../api/auth";
import { formatApiError } from "../utils/formatApiError";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const initialSalonId =
    (searchParams.get("salonId") || searchParams.get("salon_id") || "").trim();
  const [salonId, setSalonId] = useState(initialSalonId);
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [salonLookupEmail, setSalonLookupEmail] = useState("");
  const [salonLookupError, setSalonLookupError] = useState("");
  const [salonLookupSubmitting, setSalonLookupSubmitting] = useState(false);
  const [salonLookupSubmitted, setSalonLookupSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await requestStaffPasswordReset(salonId.trim(), identifier.trim());
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Staff password reset request error:", err);
      }
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const handleSalonLookupSubmit = async (event) => {
    event.preventDefault();
    setSalonLookupError("");
    setSalonLookupSubmitting(true);
    try {
      await requestStaffSalonIds(salonLookupEmail.trim());
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Staff salon ID reminder request error:", err);
      }
      setSalonLookupError(formatApiError(err));
    } finally {
      setSalonLookupSubmitting(false);
      setSalonLookupSubmitted(true);
    }
  };

  const loginTarget = salonId.trim()
    ? `/login?salonId=${encodeURIComponent(salonId.trim())}`
    : "/login";

  return (
    <div className="min-h-screen bg-[#fdeee9] flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-start px-4 pt-3 pb-6 sm:pt-4 sm:pb-8 lg:justify-center lg:py-4">
        <div className="w-full max-w-md bg-white border border-gray-200 shadow-lg rounded-lg px-8 py-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Reset your password
          </h1>
          <p className="text-sm text-slate-600">
            Enter your Salon ID and your email or username. If an account matches,
            we&apos;ll email a reset link.
          </p>

          {submitted && (
            <div className="mt-4 p-3 rounded bg-slate-50 border border-slate-200 text-slate-700 text-sm">
              If an account matches, we emailed a reset link.
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="forgot-password-salon-id"
                className="block text-sm font-medium text-gray-700"
              >
                Salon ID
              </label>
              <input
                id="forgot-password-salon-id"
                type="text"
                inputMode="numeric"
                required
                value={salonId}
                onChange={(event) => setSalonId(event.target.value)}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                placeholder="123"
              />
            </div>

            <div>
              <label
                htmlFor="forgot-password-identifier"
                className="block text-sm font-medium text-gray-700"
              >
                Email or username
              </label>
              <input
                id="forgot-password-identifier"
                type="text"
                required
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                placeholder="Enter email or username"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="rounded bg-rose-500 hover:bg-rose-600 text-white font-semibold px-4 py-2.5 text-sm shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send reset link"}
              </button>
              <Link
                to={loginTarget}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Back to sign in
              </Link>
            </div>
          </form>

          <div className="mt-8 border-t border-slate-200 pt-6">
            <h2 className="text-lg font-semibold text-gray-900">Forgot your Salon ID?</h2>
            <p className="mt-2 text-sm text-slate-600">
              Enter the email address on your Ellyanna account. For security,
              we&apos;ll only send salon IDs to the email already on file. If you belong
              to multiple salons, the email will include the full list.
            </p>

            {salonLookupSubmitted && (
              <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                If an account matches, we emailed the salon IDs linked to that email.
              </div>
            )}

            {salonLookupError && (
              <div className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                {salonLookupError}
              </div>
            )}

            <form onSubmit={handleSalonLookupSubmit} className="mt-4 space-y-4">
              <div>
                <label
                  htmlFor="forgot-password-salon-lookup-email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Account email
                </label>
                <input
                  id="forgot-password-salon-lookup-email"
                  type="email"
                  required
                  value={salonLookupEmail}
                  onChange={(event) => setSalonLookupEmail(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                  placeholder="manager@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={salonLookupSubmitting}
                className="rounded border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {salonLookupSubmitting ? "Sending..." : "Email my Salon IDs"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
